// TrueUpFilters.tsx
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { X } from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";

interface TrueUpFiltersProps {
  filters: {
    status: string;
  };
  onFiltersChange: (filters: { status: string }) => void;
  onClearFilters: () => void;
}

export const TrueUpFilters: React.FC<TrueUpFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const createFormFields = (): FormField[] => [
    {
      name: "status",
      label: "Status",
      type: "select",
      required: false,
      placeholder: "All Cases",
      options: [
        { label: "All Cases", value: "all" },
        { label: "Exception", value: "Exception" },
        { label: "Under-Billed", value: "Under-Billed" },
        { label: "Over-Billed", value: "Over-Billed" },
        { label: "Normal", value: "Normal" },
      ],
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form: UseFormReturn<{ status: string }> = FormService.createForm<{
    status: string;
  }>({
    fields: createFormFields(),
    defaultValues: filters,
  });

  // Handle form submission (Apply Filters)
  const handleSubmit = (data: { status: string }) => {
    onFiltersChange(data);
  };

  // Handle form reset (Clear Filters)
  const handleReset = () => {
    const defaultFilters = {
      status: "all",
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  // Custom footer with Apply and Clear buttons
  const renderCustomFooter = () => (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      <Button
        type="button"
        onClick={handleReset}
        variant="outline"
        className="flex items-center gap-2"
      >
        <X className="h-4 w-4" />
        Clear Filters
      </Button>

      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false, // Using custom footer
          showResetButton: false, // Using custom footer
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={1}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
